import { useEffect, useState } from "react";
import { Alarm, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { FOLLOWUP_TYPES } from "@/lib/constants";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";

export default function Snoozed() {
  const [items, setItems] = useState(null);
  const [dates, setDates] = useState({});

  const load = () => api.get("/followups").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const reschedule = async (f) => {
    const due = dates[f.id];
    if (!due) { toast.error("Pick a new date"); return; }
    try { await api.post(`/followups/${f.id}/snooze`, { due_date: due, reason: "Rescheduled from snoozed list" }); toast.success("Rescheduled"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading snoozed follow-ups…</div>;

  const snoozed = items.filter((f) => f.snoozed && f.status === "pending");

  return (
    <div className="space-y-6" data-testid="snoozed-page">
      <PageHeader overline="Follow-ups" title="Snoozed / Rescheduled" subtitle="Deferrals are kept with their reason, so nothing silently disappears" />

      {snoozed.length === 0 ? (
        <EmptyState icon={Alarm}>Nothing snoozed. Use the Snooze action on any follow-up to push it out with a reason.</EmptyState>
      ) : (
        <div className="space-y-3">
          {snoozed.map((f) => (
            <Card key={f.id} className="p-5 border-border border-l-4 border-l-amber-400" data-testid={`snoozed-${f.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-primary">{f.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {FOLLOWUP_TYPES[f.followup_type] || "Other"} · now due <span className="font-mono">{(f.due_date || "").slice(0, 10)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <DatePicker className="w-[150px]" value={dates[f.id] || ""} onChange={(v) => setDates({ ...dates, [f.id]: v })} placeholder="New date" clearable data-testid={`snooze-date-${f.id}`} />
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => reschedule(f)} data-testid={`reschedule-${f.id}`}>
                    <ArrowClockwise size={14} className="mr-1" /> Reschedule
                  </Button>
                </div>
              </div>
              {(f.snooze_history || []).length > 0 && (
                <div className="mt-3 space-y-1 border-t border-border pt-3">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">Deferral history</div>
                  {f.snooze_history.map((h, i) => (
                    <div key={`${h.at}-${i}`} className="text-xs text-muted-foreground">
                      <span className="font-mono">{(h.from || "—").slice(0, 10)} → {(h.to || "").slice(0, 10)}</span>
                      {h.reason ? ` · ${h.reason}` : ""}{h.by ? ` · ${h.by}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
